'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  IconLayoutDashboard,
  IconFolder,
  IconDatabase,
  IconMenu2,
  IconX,
  IconRefresh,
  IconPlayerPlay,
} from '@tabler/icons-react';
import {
  getHeaderConfig,
  DC_REFRESH_EVENT,
  dispatchDCEvent,
} from '@/lib/dashboard/header-config';
import { HeaderSlotProvider, useHeaderSlot } from '@/lib/dashboard/header-slot-context';
import { ROUTES } from '@/lib/routes';

const navItems = [
  { name: 'Overview', href: ROUTES.dashboard, icon: IconLayoutDashboard },
  { name: 'Projects', href: ROUTES.projects, icon: IconFolder },
  { name: 'Sources', href: ROUTES.sources, icon: IconDatabase },
];

function DashboardShell({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pathname = usePathname();
  const headerConfig = getHeaderConfig(pathname);
  const { centerSlot } = useHeaderSlot();

  const handleRefresh = useCallback(() => {
    dispatchDCEvent(DC_REFRESH_EVENT);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile top nav */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between p-4 border-b bg-background/80 backdrop-blur-md">
        <span className="font-semibold text-lg">DC Project Explorer</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <IconX size={20} /> : <IconMenu2 size={20} />}
        </Button>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64 bg-card border-r transform transition-transform duration-200 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="p-6 flex flex-col items-center gap-4">
          <Image
            src="/ipd-logo.svg"
            alt="IPD Logo"
            width={120}
            height={60}
            className="h-12 w-auto"
          />
          <h1 className="font-bold text-xl text-center">DC Project Explorer</h1>
        </div>
        <Separator />
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.href === ROUTES.dashboard
                ? pathname === ROUTES.dashboard
                : pathname.startsWith(item.href);
            return (
              <Link
                key={item.name}
                href={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
              >
                <Icon size={18} />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Header */}
        <header
          className="border-b bg-background/80 backdrop-blur-md supports-backdrop-filter:bg-background/60 sticky top-0 z-40"
          style={{ '--header-height': '72px' } as React.CSSProperties}
        >
          <div className="flex items-center gap-3 p-2 lg:p-4 min-h-[56px]">
            {/* Title */}
            <div className="shrink-0">
              <h1 className="text-xl font-bold leading-tight">{headerConfig.title}</h1>
            </div>
            {/* Center slot (search bar injected by page) */}
            {centerSlot && (
              <div className="flex-1 min-w-0 hidden sm:block">
                {centerSlot}
              </div>
            )}
            {/* Spacer when no center slot */}
            {!centerSlot && <div className="flex-1" />}
            {/* Right actions */}
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={handleRefresh}>
                <IconRefresh className="size-4 mr-1.5" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              {headerConfig.actionsVariant !== 'dashboard' && (
                <Button variant="ghost" size="sm" onClick={handleRefresh}>
                  <IconPlayerPlay className="size-4 mr-1.5" />
                  <span className="hidden sm:inline">Run explorer</span>
                </Button>
              )}
            </div>
          </div>
          {/* Mobile center slot */}
          {centerSlot && (
            <div className="sm:hidden px-2 pb-2">
              {centerSlot}
            </div>
          )}
        </header>

        {/* Page content */}
        <main className="flex-1">{children}</main>

        {/* Footer */}
        <footer className="border-t bg-card/50 mt-8">
          <div className="p-6 lg:p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Funded by */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-4">
                  Funded by
                </h3>
                <a
                  href="https://www.bmz.de/en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-opacity hover:opacity-80"
                >
                  <Image
                    src="/federal.png"
                    alt="Federal Ministry for Economic Cooperation and Development"
                    width={280}
                    height={80}
                    className="h-12 w-auto"
                  />
                </a>
              </div>

              {/* Implemented by */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-4">
                  Implemented by
                </h3>
                <div className="flex flex-wrap items-center gap-8">
                  <a
                    href="https://www.bga.de/en/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block transition-opacity hover:opacity-80"
                  >
                    <Image
                      src="/BGA.png"
                      alt="BGA - Federation of German Wholesale, Foreign Trade and Services"
                      width={160}
                      height={60}
                      className="h-10 w-auto"
                    />
                  </a>
                  <a
                    href="https://www.sequa.de/en"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block transition-opacity hover:opacity-80"
                  >
                    <Image
                      src="/Sequa.png"
                      alt="Sequa"
                      width={140}
                      height={50}
                      className="h-10 w-auto"
                    />
                  </a>
                </div>
              </div>

              {/* Build & hosted by */}
              <div className='flex flex-col md:justify-end md:items-end md:text-right'>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-4 text-left">
                    Build & hosted by
                  </h3>
                  <a
                    href="https://competa.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block transition-opacity hover:opacity-80"
                  >
                    <Image
                      src="/competa-logo.svg"
                      alt="Competa IT"
                      width={214}
                      height={41}
                      className="h-10 w-auto"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <HeaderSlotProvider>
      <DashboardShell>{children}</DashboardShell>
    </HeaderSlotProvider>
  );
}
